'use client';
import {
  AspectRatio,
  Box,
  Flex,
  Grid,
  Separator,
  useDisclosure,
} from '@chakra-ui/react';
import NextHead from 'next/head';
import { useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';

import { GooglePayScript } from 'components/common/payments/GooglePayScript';
import { UnknownErrorContext } from '~/components/common/UnknownErrorContent';
import { BackFloatButton } from '~/components/common/buttons/BackFloatButton';
import { ApplePayScript } from '~/components/common/payments/ApplePayScript';
import { EventDeliveryInfoModal } from '~/components/event/EventDeliveryInfoModal';
import { SelectDetail } from '~/components/select/SelectDetail';
import { LoginAlertModal } from '~/components/shops/LoginAlertModal';
import { useGachaIdInfo } from '~/hooks/resources/';
import { useDeliveryInfo } from '~/hooks/resources/delivery_info';
import { useGachaDeliveryBundles } from '~/hooks/resources/gacha_id_delivery_bundles';
import { useGachaPaymentMethods } from '~/hooks/resources/gacha_id_payment_methods';
import { DeliveryInfo } from '~/types/resources/deliveryInfo';
import type { GachaDetail } from '~/types/resources/gachaDetail';
import { Skeleton, SkeletonText } from '../ui/skeleton';

type Props = {
  initialGachaDetail: GachaDetail;
  uniqueName: string;
  playAmount: number;
};

const generateDeliveryInfoDefaultValues = (
  deliveryInfo: DeliveryInfo | null
): Partial<DeliveryInfo> => {
  const defaultValues = {
    country: 'JP' as DeliveryInfo['country'],
    phone_number_country_code: 'jp',
  };
  if (deliveryInfo != null) {
    const phoneNumberCountryCode =
      deliveryInfo.phone_number_country_code?.toLowerCase() ?? 'jp';
    return {
      ...defaultValues,
      ...deliveryInfo,
      phone_number_country_code:
        phoneNumberCountryCode as DeliveryInfo['phone_number_country_code'],
    };
  }
  return defaultValues;
};

export const PlayGachaContainer: React.FC<Props> = (props) => {
  const { gachaInfo: gachaDetail, error } = useGachaIdInfo(props.uniqueName, {
    initialState: props.initialGachaDetail,
  });
  const { gachaPaymentMethods } = useGachaPaymentMethods(
    gachaDetail?.id ?? null
  );
  const {
    user_attribute,
    canDeiveryInfoEdit: canDeliveryInfoEdit,
    loading: isLoadingDeliveryInfo,
  } = useDeliveryInfo();

  const deliveryForm = useForm<DeliveryInfo>({
    mode: 'onSubmit',
    reValidateMode: 'onChange',
    resolver: undefined,
    context: undefined,
    criteriaMode: 'firstError',
    shouldFocusError: true,
    defaultValues: generateDeliveryInfoDefaultValues(user_attribute ?? null),
  });

  const { deliveryBundles } = useGachaDeliveryBundles(
    gachaDetail?.id ?? null,
    deliveryForm.watch('country') ?? 'JP'
  );

  const deliveryInfoDisclosure = useDisclosure();

  const [guide_login, setGuideLogin] = useState(false);
  const [url, setUrl] = useState<object | null>(null);

  const sortedShippingSpecs = useMemo(() => {
    if (gachaDetail == undefined) return [];
    if (gachaDetail.shipping_specs == null) return [];
    return gachaDetail.shipping_specs.sort((a) => {
      if (a.country == 'JP') return -1;
      return 1;
    });
  }, [gachaDetail]);

  if (gachaDetail == undefined) {
    return <EventSelectPageSkeleton />;
  }
  if (error != null || gachaDetail == null || gachaDetail.status == 'hidden') {
    return <UnknownErrorContext title="イベント情報の取得に失敗しました" />;
  }
  return (
    <>
      <NextHead>
        <title>{`${gachaDetail?.name} - お支払い`}</title>
        <meta
          httpEquiv="Content-Security-Policy"
          content="default-src 'self' static.mul-pay.jp *.static.mul-pay.jp"
        />
      </NextHead>
      <ApplePayScript />
      <GooglePayScript />
      <Flex width="full" justifyContent="center" flexDir="column">
        <BackFloatButton href={`/e/${props.uniqueName}`} />
        <FormProvider {...deliveryForm}>
          <SelectDetail
            gachaDetail={gachaDetail}
            gachaPaymentMethods={gachaPaymentMethods ?? []}
            playAmount={props.playAmount}
            deliveryBundles={deliveryBundles}
            deliveryInfo={user_attribute ?? null}
            isLoadingDeliveryInfo={isLoadingDeliveryInfo}
            deliveryInfoOnOpen={deliveryInfoDisclosure.onOpen}
            canDeliveryInfoEdit={canDeliveryInfoEdit}
            setGuideLoginOn={() => setGuideLogin(true)}
            setUrl={setUrl}
          />
        </FormProvider>
      </Flex>
      <EventDeliveryInfoModal
        isOpen={deliveryInfoDisclosure.open}
        onClose={deliveryInfoDisclosure.onClose}
        deliveryBundles={deliveryBundles ?? []}
        deliveryInfo={user_attribute ?? null}
        shippingSpecs={sortedShippingSpecs}
      />
      <LoginAlertModal
        isOpen={guide_login}
        onClose={() => {
          setGuideLogin(false);
        }}
        title="購入にはユーザ登録する必要があります。"
        return_url={url}
      />
    </>
  );
};

const EventSelectPageSkeleton: React.FC = () => {
  return (
    <Flex w="full" alignItems="center" justifyContent="center">
      <Flex
        flex={1}
        flexDir="column"
        maxW="600px"
        w="full"
        justifyContent="center"
        mx="16px"
      >
        <Box h="32px" />
        <SkeletonText height="36px" lineClamp={1} w="240px" />
        <Box h="16px" />
        <Flex
          flexDir="row"
          border="1px"
          borderColor="border"
          p="16px"
          rounded="10px"
        >
          <Flex flex={1} flexDir="column">
            <AspectRatio ratio={16 / 9}>
              <Skeleton />
            </AspectRatio>
          </Flex>
          <Box w="12px" />
          <Flex flex={2} flexDir="column" justify="left">
            <SkeletonText height="16px" lineClamp={1} w="60%" />
            <Box h="4px" />
            <SkeletonText height="48px" lineClamp={1} w="90%" />
            <Box h="4px" />
            <SkeletonText height="48px" lineClamp={1} w="90%" />
            <Flex flexGrow={1} justifyContent="flex-end" alignItems="center">
              <Skeleton h="24px" />
            </Flex>
            <Box h="8px" />
            <Flex>
              <Separator />
            </Flex>
            <Box h="8px" />
            <SkeletonText height="48px" lineClamp={1} w="160px" />
          </Flex>
        </Flex>
        <Box h="32px" />
        <SkeletonText height="32px" lineClamp={1} w="160px" />
        <Box h="32px" />
        <Flex w="full" justifyContent="center">
          <SkeletonText height="32px" lineClamp={1} w="80%" />
        </Flex>
        <Box h="32px" />
        <Flex>
          <Separator />
        </Flex>
        <Box h="32px" />
        <Flex w="full" justifyContent="center">
          <SkeletonText height="32px" lineClamp={1} w="80%" />
        </Flex>
        <Box h="32px" />
        <Flex>
          <Separator />
        </Flex>
        <Box h="32px" />
        <Flex w="full" justifyContent="center">
          <SkeletonText height="32px" lineClamp={1} w="80%" />
        </Flex>
        <Box h="32px" />
        <Flex>
          <Separator />
        </Flex>
        <Box h="32px" />
        <Flex w="full" justifyContent="center">
          <SkeletonText height="32px" lineClamp={1} w="80%" />
        </Flex>
        <Box h="32px" />
        <Flex>
          <Separator />
        </Flex>
        <Box h="32px" />
        <Flex w="full" justifyContent="center">
          <SkeletonText height="32px" lineClamp={1} w="80%" />
        </Flex>
        <Box h="32px" />
        <Flex>
          <Separator />
        </Flex>
        <Box h="32px" />
        <Flex w="full" justifyContent="center">
          <SkeletonText height="32px" lineClamp={1} w="160px" />
        </Flex>
        <Box h="32px" />
        <Grid templateColumns="repeat(2, 1fr)" gap="8px" mx="8px">
          {[...Array(5)].map((_, index) => {
            return (
              <AspectRatio ratio={16 / 9} key={index}>
                <Skeleton h="full" w="full" />
              </AspectRatio>
            );
          })}
        </Grid>
        <Box h="32px" />
        <Skeleton h="240px" w="full" />
        <Box h="32px" />
        <SkeletonText height="20px" lineClamp={1} w="160px" />
        <Box h="16px" />
        <Flex px="16px" w="full" justifyContent="center">
          <SkeletonText height="36px" lineClamp={1} w="240px" />
        </Flex>
        <Box h="32px" />
        <Flex px="16px" w="full" justifyContent="center">
          <SkeletonText height="36px" lineClamp={1} w="160px" />
        </Flex>
        <Box h="80px" />
      </Flex>
    </Flex>
  );
};
